import React, { useState, useEffect } from 'react';
import { Session } from '@supabase/supabase-js';
import { supabase } from '../../supabaseClient';

interface BatchJobViewerProps {
  session: Session;
  batchJobId?: string;
}

interface BatchJob {
  id: string;
  name: string;
  description: string;
  total_documents: number;
  processed_documents: number;
  failed_documents: number;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
}

interface BatchDocument {
  id: string;
  file_name: string;
  status: string;
  analysis_result: any;
  error_message: string | null;
  completed_at: string | null;
}

interface ComparativeResult {
  comparison_type: string;
  summary: string;
  risk_distribution: {
    high: number;
    medium: number;
    low: number;
  };
  findings: any[];
}

export const BatchJobViewer: React.FC<BatchJobViewerProps> = ({ session, batchJobId }) => {
  const [jobs, setJobs] = useState<BatchJob[]>([]);
  const [selectedJob, setSelectedJob] = useState<BatchJob | null>(null);
  const [documents, setDocuments] = useState<BatchDocument[]>([]);
  const [comparativeResults, setComparativeResults] = useState<ComparativeResult | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedDocument, setSelectedDocument] = useState<BatchDocument | null>(null);

  // Fetch all batch jobs
  const fetchJobs = async () => {
    const { data, error } = await supabase
      .from('batch_analysis_jobs')
      .select('*')
      .eq('user_id', session.user.id)
      .order('created_at', { ascending: false });

    if (!error && data) {
      setJobs(data);
      
      // Auto-select job if provided
      if (batchJobId) {
        const job = data.find(j => j.id === batchJobId);
        if (job) {
          setSelectedJob(job);
          fetchJobDetails(batchJobId);
        }
      }
    }
    setIsLoading(false);
  };

  // Fetch job details
  const fetchJobDetails = async (jobId: string) => {
    // Fetch documents
    const { data: docsData } = await supabase
      .from('batch_analysis_documents')
      .select('*')
      .eq('batch_job_id', jobId)
      .order('processing_order', { ascending: true });

    if (docsData) {
      setDocuments(docsData);
    }

    // Fetch comparative results
    const { data: compData } = await supabase
      .from('batch_comparative_results')
      .select('*')
      .eq('batch_job_id', jobId)
      .eq('comparison_type', 'overall')
      .single();

    if (compData) {
      setComparativeResults(compData);
    }
  };

  useEffect(() => {
    fetchJobs();
    
    // Set up realtime subscription for job updates
    const jobsSubscription = supabase
      .channel('batch_jobs_channel')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'batch_analysis_jobs',
          filter: `user_id=eq.${session.user.id}`
        },
        () => {
          fetchJobs();
        }
      )
      .subscribe();

    return () => {
      jobsSubscription.unsubscribe();
    };
  }, [session.user.id]);

  // Auto-refresh while processing
  useEffect(() => {
    if (selectedJob?.status === 'processing') {
      const interval = setInterval(() => {
        if (selectedJob.id) {
          fetchJobDetails(selectedJob.id);
        }
      }, 3000);
      
      return () => clearInterval(interval);
    }
    return undefined;
  }, [selectedJob?.id, selectedJob?.status]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200';
      case 'processing': return 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200';
      case 'failed': return 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200';
      case 'cancelled': return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
      default: return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200';
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const calculateProgress = (job: BatchJob) => {
    if (job.total_documents === 0) return 0;
    return Math.round(((job.processed_documents + job.failed_documents) / job.total_documents) * 100);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <span className="material-symbols-outlined animate-spin text-4xl text-luna-secondary">sync</span>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Jobs List */}
      <div className="lg:col-span-1">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Stapelanalysen
          </h3>

          {jobs.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">
              Keine Stapelanalysen gefunden
            </p>
          ) : (
            <div className="space-y-2">
              {jobs.map((job) => (
                <button
                  key={job.id}
                  onClick={() => {
                    setSelectedJob(job);
                    fetchJobDetails(job.id);
                  }}
                  className={`w-full text-left p-3 rounded-lg border transition-colors ${
                    selectedJob?.id === job.id
                      ? 'border-luna-secondary bg-luna-secondary/10'
                      : 'border-gray-200 dark:border-gray-600 hover:border-luna-secondary/50'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-semibold text-sm text-gray-900 dark:text-white">
                      {job.name}
                    </h4>
                    <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(job.status)}`}>
                      {job.status}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                    <span>{job.total_documents} Dokumente</span>
                    <span>•</span>
                    <span>{calculateProgress(job)}%</span>
                  </div>
                  
                  {job.status === 'processing' && (
                    <div className="mt-2 w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                      <div
                        className="bg-gradient-to-r from-luna-primary to-luna-secondary h-1.5 rounded-full transition-all"
                        style={{ width: `${calculateProgress(job)}%` }}
                      />
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Job Details */}
      <div className="lg:col-span-2">
        {!selectedJob ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-8 text-center">
            <span className="material-symbols-outlined text-6xl text-gray-300 dark:text-gray-600 mb-4">
              folder_open
            </span>
            <p className="text-gray-500 dark:text-gray-400">
              Wählen Sie eine Stapelanalyse aus, um Details anzuzeigen
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Job Overview */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                    {selectedJob.name}
                  </h2>
                  {selectedJob.description && (
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {selectedJob.description}
                    </p>
                  )}
                </div>
                <span className={`px-3 py-1 text-sm rounded-full ${getStatusColor(selectedJob.status)}`}>
                  {selectedJob.status}
                </span>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div className="text-center p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">
                    {selectedJob.total_documents}
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-400">Gesamt</div>
                </div>
                <div className="text-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <div className="text-2xl font-bold text-green-800 dark:text-green-200">
                    {selectedJob.processed_documents}
                  </div>
                  <div className="text-xs text-green-600 dark:text-green-400">Verarbeitet</div>
                </div>
                <div className="text-center p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                  <div className="text-2xl font-bold text-red-800 dark:text-red-200">
                    {selectedJob.failed_documents}
                  </div>
                  <div className="text-xs text-red-600 dark:text-red-400">Fehlgeschlagen</div>
                </div>
                <div className="text-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <div className="text-2xl font-bold text-blue-800 dark:text-blue-200">
                    {calculateProgress(selectedJob)}%
                  </div>
                  <div className="text-xs text-blue-600 dark:text-blue-400">Fortschritt</div>
                </div>
              </div>

              <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                <div>Erstellt: {formatDate(selectedJob.created_at)}</div>
                {selectedJob.started_at && <div>Gestartet: {formatDate(selectedJob.started_at)}</div>}
                {selectedJob.completed_at && <div>Abgeschlossen: {formatDate(selectedJob.completed_at)}</div>}
              </div>
            </div>

            {/* Comparative Analysis */}
            {comparativeResults && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Vergleichende Analyse
                </h3>
                
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="text-center p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border-l-4 border-red-500">
                    <div className="text-3xl font-bold text-red-800 dark:text-red-200">
                      {comparativeResults.risk_distribution.high}
                    </div>
                    <div className="text-sm text-red-600 dark:text-red-400">Hohes Risiko</div>
                  </div>
                  <div className="text-center p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border-l-4 border-yellow-500">
                    <div className="text-3xl font-bold text-yellow-800 dark:text-yellow-200">
                      {comparativeResults.risk_distribution.medium}
                    </div>
                    <div className="text-sm text-yellow-600 dark:text-yellow-400">Mittleres Risiko</div>
                  </div>
                  <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border-l-4 border-green-500">
                    <div className="text-3xl font-bold text-green-800 dark:text-green-200">
                      {comparativeResults.risk_distribution.low}
                    </div>
                    <div className="text-sm text-green-600 dark:text-green-400">Niedriges Risiko</div>
                  </div>
                </div>

                <p className="text-sm text-gray-700 dark:text-gray-300">
                  {comparativeResults.summary}
                </p>
              </div>
            )}

            {/* Documents List */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Dokumente ({documents.length})
              </h3>

              <div className="space-y-2">
                {documents.map((doc) => (
                  <div
                    key={doc.id}
                    onClick={() => setSelectedDocument(doc)}
                    className="p-4 border border-gray-200 dark:border-gray-600 rounded-lg hover:border-luna-secondary cursor-pointer transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <span className={`material-symbols-outlined ${
                          doc.status === 'completed' ? 'text-green-500' :
                          doc.status === 'failed' ? 'text-red-500' :
                          doc.status === 'processing' ? 'text-blue-500 animate-spin' :
                          'text-gray-400'
                        }`}>
                          {doc.status === 'completed' ? 'check_circle' :
                           doc.status === 'failed' ? 'error' :
                           doc.status === 'processing' ? 'sync' : 'pending'}
                        </span>
                        
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {doc.file_name}
                          </p>
                          {doc.error_message && (
                            <p className="text-xs text-red-600 dark:text-red-400">
                              {doc.error_message}
                            </p>
                          )}
                        </div>
                      </div>

                      {doc.analysis_result && (
                        <span className={`px-2 py-1 text-xs rounded ${
                          doc.analysis_result.overallRisk === 'high' ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200' :
                          doc.analysis_result.overallRisk === 'medium' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200' :
                          'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200'
                        }`}>
                          {doc.analysis_result.overallRisk}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Document Analysis Modal */}
      {selectedDocument && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-6 flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                {selectedDocument.file_name}
              </h3>
              <button
                onClick={() => setSelectedDocument(null)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>

            <div className="p-6">
              {selectedDocument.analysis_result ? (
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Risikoanalyse</h4>
                    <div className="flex items-center gap-4">
                      <span className={`px-3 py-1 rounded-full ${
                        selectedDocument.analysis_result.overallRisk === 'high' ? 'bg-red-100 text-red-800' :
                        selectedDocument.analysis_result.overallRisk === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                        {selectedDocument.analysis_result.overallRisk}
                      </span>
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        Risiko-Score: {selectedDocument.analysis_result.riskScore}/100
                      </span>
                    </div>
                  </div>

                  {selectedDocument.analysis_result.keyFindings?.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Wichtige Erkenntnisse</h4>
                      <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 dark:text-gray-300">
                        {selectedDocument.analysis_result.keyFindings.map((finding: string, idx: number) => (
                          <li key={idx}>{finding}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {selectedDocument.analysis_result.recommendations?.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Empfehlungen</h4>
                      <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 dark:text-gray-300">
                        {selectedDocument.analysis_result.recommendations.map((rec: string, idx: number) => (
                          <li key={idx}>{rec}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">
                  Keine Analyseergebnisse verfügbar
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
